import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  Fab,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Joyride } from "react-joyride";
import { DateTime } from "luxon";

import { obtenerColorFestivo, esColorOscuro } from "Auxiliares/Colores";
import {
  peticionGet,
  peticionPost,
  peticionDelete,
} from "Componentes/ComponenteAxios";
import { useAsignacionPlantillaApi } from "hooks/useAsignacionPlantillaApi";

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// Obtiene los días del mes con semanas completas (lunes a domingo)
const getDiasDelMes = (anio, mes) => {
  const primerDia = DateTime.fromObject({ year: anio, month: mes + 1, day: 1 });
  const ultimoDia = primerDia.endOf("month").startOf("day");
  const dias = [];

  // Retroceder hasta el lunes de la primera semana
  let inicio = primerDia;
  while (inicio.weekday !== 1) {
    inicio = inicio.minus({ days: 1 });
  }

  // Avanzar hasta el domingo de la última semana
  let fin = ultimoDia;
  while (fin.weekday !== 7) {
    fin = fin.plus({ days: 1 });
  }

  let cursor = inicio;
  while (cursor <= fin) {
    dias.push({
      numero: cursor.day,
      fecha: cursor.toISODate(),
      diaSemana: cursor.setLocale("es").weekdayShort,
      esFinDeSemana: cursor.weekday >= 6,
      fueraDeMes: cursor.month !== mes + 1 || cursor.year !== anio,
    });
    cursor = cursor.plus({ days: 1 });
  }

  return dias;
};

const VistaMensualDepartamentos = () => {
  const { getAsignaciones, getTrabajadores } = useAsignacionPlantillaApi();

  const [asignaciones, setAsignaciones] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCuadricula, setLoadingCuadricula] = useState(false);
  const [error, setError] = useState(null);
  const [anioSeleccionado, setAnioSeleccionado] = useState("");
  const [tabSede, setTabSede] = useState(0);
  const [departamentoPadreId, setDepartamentoPadreId] = useState(null);
  const [departamentoHijoId, setDepartamentoHijoId] = useState(null);
  const [mesActivo, setMesActivo] = useState(0);

  // Datos de la cuadrícula
  const [datosMes, setDatosMes] = useState([]); // usuarios con sus asignaciones diarias
  const [mapaAgrupaciones, setMapaAgrupaciones] = useState({}); // turno_id -> codigo
  const [agrupacionesSede, setAgrupacionesSede] = useState([]); // lista completa de agrupaciones
  const [festivos, setFestivos] = useState({}); // fecha -> { tipo, nombre }
  const [usuarioModal, setUsuarioModal] = useState(null); // usuario seleccionado para el modal
  const [menuContextual, setMenuContextual] = useState(null); // { anchorPosition, usuario, fecha }
  const [usuarioEliminarFila, setUsuarioEliminarFila] = useState(null); // usuario para confirmar borrado de fila
  const [todosUsuarios, setTodosUsuarios] = useState([]); // todos los usuarios del endpoint (sin filtrar)
  const [openAgregarTrabajador, setOpenAgregarTrabajador] = useState(false);
  const [busquedaTrabajador, setBusquedaTrabajador] = useState("");
  const [runTour, setRunTour] = useState(false);
  const [mostrarOtrasSedes, setMostrarOtrasSedes] = useState(false);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [puestosTrabajo, setPuestosTrabajo] = useState([]); // lista completa de puestos
  const [puestosSeleccionados, setPuestosSeleccionados] = useState(null); // null = aún no inicializado
  const [mostrarSinPuesto, setMostrarSinPuesto] = useState(true);
  const [usuariosManuales, setUsuariosManuales] = useState([]); // añadidos manualmente con el botón (+)

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const [asignacionesResp, deptResp, trabajadoresResp, puestosResp] = await Promise.all([
          getAsignaciones(),
          peticionGet("/departamentos"),
          getTrabajadores(),
          peticionGet("/puestos-trabajo"),
        ]);
        setAsignaciones(asignacionesResp || []);
        const depts = deptResp?.peticion?.datos ?? deptResp?.peticion ?? [];
        setDepartamentos(Array.isArray(depts) ? depts : []);
        const trabs = Array.isArray(trabajadoresResp) ? trabajadoresResp : [];
        setTodosUsuarios(
          trabs
            .map((t) => ({
              id_usuario: t.id,
              nombre: t.nombre ?? "",
              apellidos: t.apellidos ?? "",
              asignacionesPorFecha: {},
            }))
            .sort((a, b) => {
              const nA = `${a.nombre} ${a.apellidos}`.toLowerCase();
              const nB = `${b.nombre} ${b.apellidos}`.toLowerCase();
              return nA.localeCompare(nB);
            }),
        );
        const puestos = puestosResp?.peticion?.datos ?? puestosResp?.peticion ?? [];
        const listaPuestos = Array.isArray(puestos) ? puestos : [];
        setPuestosTrabajo(listaPuestos);
        setPuestosSeleccionados(new Set(listaPuestos.map((p) => p.id)));
      } catch (e) {
        console.error(e);
        setError("Error cargando datos.");
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [getAsignaciones, getTrabajadores]);

  const aniosDisponibles = useMemo(() => {
    const actual = new Date().getFullYear();
    const anios = new Set([actual - 1, actual, actual + 1]);
    asignaciones.forEach((a) => {
      if (a.fecha_inicio) anios.add(new Date(a.fecha_inicio).getFullYear());
      if (a.fecha_fin) anios.add(new Date(a.fecha_fin).getFullYear());
    });
    return [...anios].sort((a, b) => b - a);
  }, [asignaciones]);

  useEffect(() => {
    if (!anioSeleccionado && aniosDisponibles.length > 0) {
      setAnioSeleccionado(new Date().getFullYear());
    }
  }, [aniosDisponibles, anioSeleccionado]);

  // Agrupar sedes únicas a partir de los departamentos
  const sedes = useMemo(() => {
    const mapaS = new Map();
    departamentos.forEach((d) => {
      if (d.id_sede && !mapaS.has(d.id_sede)) {
        mapaS.set(d.id_sede, {
          id: d.id_sede,
          nombre: d.sede?.nombre ?? `Sede ${d.id_sede}`,
        });
      }
    });
    return [...mapaS.values()];
  }, [departamentos]);

  const sedeActual = sedes[tabSede];
  const hayMultiplesSedes = sedes.length > 1;

  const tourSteps = useMemo(() => {
    const steps = [
      {
        target: '[data-tour="select-anio"]',
        content: "Selecciona el año para el que quieres ver la planificación.",
        disableBeacon: true,
      },
      {
        target: '[data-tour="leyenda"]',
        content:
          "Aquí se muestra la leyenda de símbolos: cada código de color representa una agrupación de turnos con sus horarios.",
      },
    ];

    if (hayMultiplesSedes) {
      steps.push({
        target: '[data-tour="tabs-sedes"]',
        content:
          "Si tienes varias sedes, cambia entre ellas con estas pestañas.",
      });
    }

    steps.push(
      {
        target: '[data-tour="tabs-departamentos"]',
        content:
          "Selecciona el departamento que quieres consultar. Cada pestaña muestra los trabajadores de ese departamento con turnos asignados",
      },
      {
        target: '[data-tour="tabs-meses"]',
        content:
          "Navega entre los meses del año. Al cambiar de departamento o sede, el mes seleccionado se mantiene.",
      },
      {
        target: '[data-tour="cuadricula"]',
        content:
          "Esta es la cuadrícula mensual. Cada fila es un trabajador y cada columna un día del mes. Los símbolos indican el turno asignado.",
      },
      {
        target: '[data-tour="cuadricula"]',
        content:
          "Haz clic derecho en una celda para cambiar el turno o liberar el día. Haz clic en el nombre de un empleado para ver su resumen mensual.",
      },
      {
        target: '[data-tour="btn-agregar-trabajador"]',
        content:
          "Pulsa aquí para añadir un trabajador que no aparece en la cuadrícula.",
      },
    );

    return steps;
  }, [hayMultiplesSedes]);

  // Departamentos filtrados por la sede activa
  const departamentosFiltrados = useMemo(() => {
    if (!sedeActual) return departamentos;
    return departamentos.filter((d) => d.id_sede === sedeActual.id);
  }, [departamentos, sedeActual]);

  // Árbol de departamentos: padres (sin padre) e hijos agrupados por padre
  const padres = useMemo(
    () => departamentosFiltrados.filter((d) => !d.departamento_padre_id),
    [departamentosFiltrados],
  );

  const hijosPorPadre = useMemo(() => {
    const mapa = new Map();
    departamentosFiltrados.forEach((d) => {
      if (d.departamento_padre_id) {
        if (!mapa.has(d.departamento_padre_id)) mapa.set(d.departamento_padre_id, []);
        mapa.get(d.departamento_padre_id).push(d);
      }
    });
    return mapa;
  }, [departamentosFiltrados]);

  const departamentoActual = useMemo(() => {
    const padre = padres.find((p) => p.id === departamentoPadreId) ?? padres[0];
    if (!padre) return null;
    const hijos = hijosPorPadre.get(padre.id) ?? [];
    if (hijos.length === 0) return padre;
    return hijos.find((h) => h.id === departamentoHijoId) ?? hijos[0];
  }, [departamentoPadreId, departamentoHijoId, padres, hijosPorPadre]);

  // Cargar agrupaciones solo cuando cambia la sede
  useEffect(() => {
    const sedeId = departamentoActual?.id_sede;
    if (!sedeId) {
      setMapaAgrupaciones({});
      return;
    }

    const cargar = async () => {
      try {
        const { peticion: agrupacionesResp } = await peticionGet(
          `/agrupacionesTurnos/sede/${sedeId}`,
        );
        const agrupaciones = agrupacionesResp?.datos ?? agrupacionesResp ?? [];
        const mapa = {};
        (Array.isArray(agrupaciones) ? agrupaciones : []).forEach((agr) => {
          if (Array.isArray(agr.turnos)) {
            agr.turnos.forEach((turno) => {
              const turnoId = typeof turno === "object" ? turno.id : turno;
              mapa[turnoId] = agr.codigo;
            });
          }
        });
        setMapaAgrupaciones(mapa);
        setAgrupacionesSede(Array.isArray(agrupaciones) ? agrupaciones : []);
      } catch (e) {
        console.error("Error cargando agrupaciones", e);
      }
    };
    cargar();
  }, [departamentoActual?.id_sede]);

  // Cargar datos cuando cambia año, departamento o mes
  const cargarDatosMes = useCallback(async () => {
    if (!anioSeleccionado || !departamentoActual?.id_sede) {
      setDatosMes([]);
      return;
    }

    try {
      setLoadingCuadricula(true);
      const sedeId = departamentoActual.id_sede;
      const deptId = departamentoActual.id;
      const mes = mesActivo + 1;

      // Calcular rango completo de semanas visibles
      const dias = getDiasDelMes(anioSeleccionado, mesActivo);
      const fechaInicio = dias[0].fecha;
      const fechaFin = dias[dias.length - 1].fecha;

      // Una sola petición que cubre todos los días visibles (incluido relleno)
      const res = await peticionGet(
        `/planificador/${sedeId}/${fechaInicio}/${fechaFin}/${deptId}/fechas`,
      );
      const datos = res?.peticion?.datos ?? res?.peticion ?? [];

      const usuariosPorId = {};
      const mapaFestivos = {};
      (Array.isArray(datos) ? datos : []).forEach((usuario) => {
        if (!usuariosPorId[usuario.id_usuario]) {
          usuariosPorId[usuario.id_usuario] = {
            id_usuario: usuario.id_usuario,
            nombre: usuario.nombre,
            apellidos: usuario.apellidos,
            puesto_trabajo: usuario.puesto_trabajo ?? null,
            asignacionesPorFecha: {},
          };
        }
        // Extraer festivos (solo una vez, son iguales para todos los usuarios de la sede)
        if (Object.keys(mapaFestivos).length === 0 && usuario.completo?.festivos) {
          Object.entries(usuario.completo.festivos).forEach(([fecha, info]) => {
            mapaFestivos[fecha] = {
              tipo: info.tipo?.nombre ?? "",
              tipoId: info.tipo_festivo,
            };
          });
        }
        if (Array.isArray(usuario.detalle)) {
          usuario.detalle.forEach((d) => {
            if (d.asignaciones?.length > 0 || d.otras_sedes?.length > 0 || d.ausencia) {
              usuariosPorId[usuario.id_usuario].asignacionesPorFecha[d.fecha] =
                {
                  asignaciones: d.asignaciones || [],
                  otrasSedes: d.otras_sedes || [],
                  ausencia: d.ausencia || null,
                };
            }
          });
        }
      });
      setFestivos(mapaFestivos);

      const usuariosConDatos = Object.values(usuariosPorId).sort((a, b) => {
        const nA = `${a.nombre} ${a.apellidos}`.toLowerCase();
        const nB = `${b.nombre} ${b.apellidos}`.toLowerCase();
        return nA.localeCompare(nB);
      });
      setDatosMes(usuariosConDatos);
    } catch (e) {
      console.error(e);
      setError("Error cargando datos del mes.");
    } finally {
      setLoadingCuadricula(false);
    }
  }, [anioSeleccionado, mesActivo, departamentoActual]);

  useEffect(() => {
    setUsuariosManuales([]);
    cargarDatosMes();
  }, [cargarDatosMes]);

  const diasDelMes = useMemo(
    () => (anioSeleccionado ? getDiasDelMes(anioSeleccionado, mesActivo) : []),
    [anioSeleccionado, mesActivo],
  );

  // Filtrar usuarios visibles según el checkbox + usuarios añadidos manualmente
  const datosMesFiltrados = useMemo(() => {
    const fechasVisibles = new Set(diasDelMes.map((d) => d.fecha));
    const idsManuales = new Set(usuariosManuales.map((u) => u.id_usuario));
    const filtrados = datosMes.filter((u) => {
      // Filtro por puesto: si puestosSeleccionados está inicializado, aplicar
      if ((mostrarTodos || mostrarOtrasSedes) && puestosSeleccionados !== null && puestosTrabajo.length > 0) {
        const idPuesto = u.puesto_trabajo?.id ?? null;
        const pasaFiltro = idPuesto === null ? mostrarSinPuesto : puestosSeleccionados.has(idPuesto);
        if (!pasaFiltro) return false;
      }
      return (
        mostrarTodos ||
        idsManuales.has(u.id_usuario) ||
        Object.entries(u.asignacionesPorFecha).some(
          ([f, datos]) =>
            fechasVisibles.has(f) &&
            (datos.asignaciones.length > 0 ||
              (mostrarOtrasSedes && datos.otrasSedes.length > 0)),
        )
      );
    });
    // Añadir manuales que no vinieron del backend
    const idsPresentes = new Set(filtrados.map((u) => u.id_usuario));
    const manualesFaltantes = usuariosManuales.filter((u) => !idsPresentes.has(u.id_usuario));
    return [...filtrados, ...manualesFaltantes].sort((a, b) => {
      const nA = `${a.nombre} ${a.apellidos}`.toLowerCase();
      const nB = `${b.nombre} ${b.apellidos}`.toLowerCase();
      return nA.localeCompare(nB);
    });
  }, [datosMes, diasDelMes, mostrarOtrasSedes, mostrarTodos, usuariosManuales, puestosSeleccionados, puestosTrabajo, mostrarSinPuesto]);

  // Trabajadores disponibles para añadir (no están ya en datosMesFiltrados)
  const trabajadoresDisponibles = useMemo(() => {
    const idsEnCuadricula = new Set(datosMesFiltrados.map((u) => u.id_usuario));
    const lista = todosUsuarios.filter(
      (u) => !idsEnCuadricula.has(u.id_usuario),
    );
    if (!busquedaTrabajador.trim()) return lista;
    const q = busquedaTrabajador.toLowerCase();
    return lista.filter(
      (u) =>
        u.nombre?.toLowerCase().includes(q) ||
        u.apellidos?.toLowerCase().includes(q),
    );
  }, [todosUsuarios, datosMesFiltrados, busquedaTrabajador]);

  const handleAgregarTrabajador = (usuario) => {
    setUsuariosManuales((prev) => [...prev, usuario]);
    setOpenAgregarTrabajador(false);
    setBusquedaTrabajador("");
  };

  // Calcular resumen del empleado seleccionado
  const handleLiberarDia = async (usuario, fecha) => {
    const idSede = departamentoActual?.id_sede;
    const idDepartamento = departamentoActual?.id;
    try {
      const res = await peticionDelete("/planificador/eliminar-asignacion", {
        id_usuario: usuario.id_usuario,
        fecha,
        id_sede: idSede,
        departamento_id: idDepartamento,
      });
      if (!res.error) {
        setDatosMes((prev) =>
          prev.map((u) => {
            if (u.id_usuario !== usuario.id_usuario) return u;
            const nuevas = { ...u.asignacionesPorFecha };
            delete nuevas[fecha];
            return { ...u, asignacionesPorFecha: nuevas };
          }),
        );
      }
    } catch (e) {
      console.error("Error al liberar día", e);
    }
  };

  const handleCambiarSimbolo = async (usuario, fecha, agrupacion) => {
    const idSede = departamentoActual?.id_sede;
    const idDepartamento = departamentoActual?.id;
    try {
      const res = await peticionPost("/planificador/modificar-asignacion", {
        id_usuario: usuario.id_usuario,
        fecha,
        id_sede: idSede,
        departamento_id: idDepartamento,
        simbolo: agrupacion.codigo,
      });
      if (!res.error) {
        // Construir asignaciones locales a partir de los turnos de la agrupación
        const nuevasAsignaciones = (agrupacion.turnos || []).map((t) => ({
          turno_id: t.id,
          hora_entrada: t.inicio ?? t.hora_entrada ?? "",
          hora_salida: t.fin ?? t.hora_salida ?? "",
        }));
        setDatosMes((prev) =>
          prev.map((u) => {
            if (u.id_usuario !== usuario.id_usuario) return u;
            const nuevas = { ...u.asignacionesPorFecha };
            nuevas[fecha] = {
              asignaciones: nuevasAsignaciones,
              otrasSedes: nuevas[fecha]?.otrasSedes || [],
            };
            return { ...u, asignacionesPorFecha: nuevas };
          }),
        );
      }
    } catch (e) {
      console.error("Error al modificar asignación", e);
    }
  };

  const handleEliminarFila = async () => {
    if (!usuarioEliminarFila || !departamentoActual) return;
    const mesISO = String(mesActivo + 1).padStart(2, "0");
    const fechaInicio = `${anioSeleccionado}-${mesISO}-01`;
    const ultimoDia = DateTime.fromObject({
      year: anioSeleccionado,
      month: mesActivo + 1,
    }).daysInMonth;
    const fechaFin = `${anioSeleccionado}-${mesISO}-${String(ultimoDia).padStart(2, "0")}`;
    try {
      const res = await peticionDelete(
        "/planificador/eliminar-asignaciones-rango",
        {
          id_usuario: usuarioEliminarFila.id_usuario,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
          id_sede: departamentoActual.id_sede,
          departamento_id: departamentoActual.id,
        },
      );
      if (!res.error) {
        cargarDatosMes();
      }
    } catch (e) {
      console.error("Error al eliminar asignaciones del mes", e);
    } finally {
      setUsuarioEliminarFila(null);
    }
  };

  const resumenModal = useMemo(() => {
    if (!usuarioModal) return null;

    const diasRealesMes = diasDelMes.filter((d) => !d.fueraDeMes);
    const totalDiasMes = diasRealesMes.length;

    // Agrupar por símbolo
    const porSimbolo = {};
    let diasTrabaja = 0;

    diasRealesMes.forEach((dia) => {
      const datosDia = usuarioModal.asignacionesPorFecha[dia.fecha] || {
        asignaciones: [],
        otrasSedes: [],
      };
      const asignacionesDia = [
        ...datosDia.asignaciones,
        ...datosDia.otrasSedes,
      ];
      if (asignacionesDia.length > 0) {
        diasTrabaja++;
        asignacionesDia.forEach((a) => {
          const codigo = mapaAgrupaciones[a.turno_id] || "?";
          if (!porSimbolo[codigo]) {
            porSimbolo[codigo] = { dias: new Set(), minutos: 0 };
          }
          porSimbolo[codigo].dias.add(dia.fecha);

          // Calcular horas del turno
          if (a.hora_entrada && a.hora_salida) {
            const inicio = DateTime.fromFormat(a.hora_entrada, "HH:mm");
            let fin = DateTime.fromFormat(a.hora_salida, "HH:mm");
            if (fin < inicio) fin = fin.plus({ days: 1 });
            porSimbolo[codigo].minutos += fin.diff(inicio, "minutes").minutes;
          }
        });
      }
    });

    const simbolos = Object.entries(porSimbolo).map(([codigo, data]) => {
      const horas = Math.floor(data.minutos / 60);
      const mins = Math.round(data.minutos % 60);
      return {
        codigo,
        dias: data.dias.size,
        horas: `${horas}h ${mins > 0 ? `${mins}m` : ""}`.trim(),
      };
    });

    return {
      simbolos,
      diasTrabaja,
      diasNoTrabaja: totalDiasMes - diasTrabaja,
    };
  }, [usuarioModal, diasDelMes, mapaAgrupaciones, mesActivo, anioSeleccionado]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <CircularProgress />
      </Box>
    );
  }

  const thStyle = {
    padding: "4px 6px",
    borderBottom: "1px solid #ccc",
    borderRight: "1px solid #eee",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "2px 4px",
    borderBottom: "1px solid #eee",
    borderRight: "1px solid #eee",
    textAlign: "center",
    fontSize: "0.75rem",
    minWidth: 28,
  };

  const tdNombreStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: 500,
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 1,
    minWidth: 180,
  };

  return (
    <Card sx={{ mb: 4 }}>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        callback={(data) => {
          if (data.status === "finished" || data.status === "skipped") {
            setRunTour(false);
          }
        }}
        locale={{
          back: "Anterior",
          close: "Cerrar",
          last: "Finalizar",
          next: "Siguiente",
          skip: "Saltar tour",
        }}
        styles={{
          options: {
            primaryColor: "#1976d2",
            zIndex: 10000,
          },
        }}
      />
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <span>Vista mensual por departamentos</span>
            <Tooltip title="Recorrido guiado">
              <IconButton size="small" onClick={() => setRunTour(true)}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl
          size="small"
          sx={{ minWidth: 150, mb: 2 }}
          data-tour="select-anio"
        >
          <InputLabel id="anio-label">Año</InputLabel>
          <Select
            labelId="anio-label"
            label="Año"
            value={anioSeleccionado}
            onChange={(e) => setAnioSeleccionado(e.target.value)}
          >
            {aniosDisponibles.map((anio) => (
              <MenuItem key={anio} value={anio}>
                {anio}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {agrupacionesSede.length > 0 && (
          <Box
            data-tour="leyenda"
            sx={{
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ mr: 0.5 }}>
              Leyenda:
            </Typography>
            {agrupacionesSede.map((agr) => {
              console.log(agr);
              const horarios = (agr.turnos || [])
                .slice()
                .sort((a, b) => (a.inicio || "").localeCompare(b.fin || ""))
                .map((t) => `${t.inicio ?? "?"} - ${t.fin ?? "?"}`)
                .join(" / ");
              return (
                <Box
                  key={agr.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      borderRadius: "4px",
                      backgroundColor: agr.color || "#1976d2",
                      color: "#fff",
                    }}
                  >
                    {agr.codigo}
                  </Box>
                  <Typography variant="caption">
                    {agr.nombre}
                    {horarios ? ` (${horarios})` : ""}
                  </Typography>
                </Box>
              );
            })}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderRadius: "4px",
                  backgroundColor: "#9e9e9e",
                  color: "#fff",
                }}
              >
                -
              </Box>
              <Typography variant="caption">Libre</Typography>
            </Box>
          </Box>
        )}

        {departamentos.length > 0 ? (
          <>
            {hayMultiplesSedes && (
              <Tabs
                data-tour="tabs-sedes"
                value={tabSede}
                onChange={(_, v) => {
                  setTabSede(v);
                  setDepartamentoPadreId(null);
                  setDepartamentoHijoId(null);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 1 }}
              >
                {sedes.map((s) => (
                  <Tab key={s.id} label={s.nombre} />
                ))}
              </Tabs>
            )}
            <Tabs
              data-tour="tabs-departamentos"
              value={departamentoPadreId ?? (padres[0]?.id ?? false)}
              onChange={(_, v) => {
                setDepartamentoPadreId(v);
                setDepartamentoHijoId(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {padres.map((p) => (
                <Tab key={p.id} value={p.id} label={p.nombre} />
              ))}
            </Tabs>
            {(hijosPorPadre.get(departamentoPadreId ?? padres[0]?.id)?.length > 0) && (
              <Tabs
                value={departamentoHijoId ?? (hijosPorPadre.get(departamentoPadreId ?? padres[0]?.id)?.[0]?.id ?? false)}
                onChange={(_, v) => setDepartamentoHijoId(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mt: 0.5 }}
              >
                {hijosPorPadre.get(departamentoPadreId ?? padres[0]?.id).map((h) => (
                  <Tab key={h.id} value={h.id} label={h.nombre} />
                ))}
              </Tabs>
            )}

            <Box sx={{ mt: 2 }}>
              <Tabs
                data-tour="tabs-meses"
                value={mesActivo}
                onChange={(_, v) => setMesActivo(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                {MESES.map((mes, i) => (
                  <Tab key={i} label={mes} />
                ))}
              </Tabs>

              {/* Botón añadir trabajador */}
              {!!departamentoActual && (
                <Box sx={{ mt: 1, mb: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Fab
                      data-tour="btn-agregar-trabajador"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setBusquedaTrabajador("");
                        setOpenAgregarTrabajador(true);
                      }}
                    >
                      <AddIcon />
                    </Fab>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={mostrarOtrasSedes}
                          onChange={(e) => setMostrarOtrasSedes(e.target.checked)}
                        />
                      }
                      label="Mostrar trabajadores de otras sedes y/o departamentos"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={mostrarTodos}
                          onChange={(e) => setMostrarTodos(e.target.checked)}
                        />
                      }
                      label="Mostrar todos los trabajadores"
                    />
                  </Box>
                  {(mostrarTodos || mostrarOtrasSedes) && <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, pl: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={mostrarSinPuesto}
                        onChange={(e) => setMostrarSinPuesto(e.target.checked)}
                      />
                    }
                    label="Sin puesto asignado"
                  />
                  {puestosTrabajo.map((puesto) => (
                    <FormControlLabel
                      key={puesto.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={puestosSeleccionados?.has(puesto.id) ?? true}
                          onChange={(e) => {
                            setPuestosSeleccionados((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(puesto.id);
                              else next.delete(puesto.id);
                              return next;
                            });
                          }}
                          sx={{ color: puesto.color, "&.Mui-checked": { color: puesto.color } }}
                        />
                      }
                      label={puesto.nombre}
                    />
                  ))}
                  </Box>}
                </Box>
              )}

              <Box data-tour="cuadricula" sx={{ mt: 1 }}>
                {loadingCuadricula ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      overflowX: "auto",
                      border: "1px solid #ddd",
                      borderRadius: 1,
                    }}
                  >
                    <table
                      style={{ borderCollapse: "collapse", width: "100%" }}
                    >
                      <thead>
                        <tr style={{ background: "#f5f5f5" }}>
                          <th
                            style={{
                              ...thStyle,
                              position: "sticky",
                              left: 0,
                              background: "#f5f5f5",
                              zIndex: 2,
                              minWidth: 180,
                            }}
                          >
                            Empleado
                          </th>
                          {diasDelMes.map((dia) => {
                            const infoFestivo = festivos[dia.fecha];
                            const colorFestivo = infoFestivo
                              ? obtenerColorFestivo(infoFestivo.tipoId)
                              : null;
                            return (
                              <th
                                key={dia.fecha}
                                style={{
                                  ...thStyle,
                                  background: colorFestivo
                                    || (dia.fueraDeMes
                                      ? "#eeeeee"
                                      : dia.esFinDeSemana
                                        ? "#e3f2fd"
                                        : "#f5f5f5"),
                                  color: colorFestivo
                                    ? (esColorOscuro(colorFestivo) ? "#fff" : "#222")
                                    : "inherit",
                                }}
                                title={infoFestivo ? infoFestivo.tipo : ""}
                              >
                                <div>{dia.diaSemana}</div>
                                <div>{dia.numero}</div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {datosMesFiltrados.length === 0 && (
                          <tr>
                            <td
                              colSpan={diasDelMes.length + 1}
                              style={{ textAlign: "center", padding: "16px", color: "#888" }}
                            >
                              No hay asignaciones para este departamento en{" "}
                              {MESES[mesActivo]} {anioSeleccionado}.
                            </td>
                          </tr>
                        )}
                        {datosMesFiltrados.map((usuario) => (
                          <tr key={usuario.id_usuario}>
                            <td
                              style={{
                                ...tdNombreStyle,
                                cursor: "pointer",
                                color: "#1565c0",
                                textDecoration: "underline",
                                background: usuario.puesto_trabajo?.color
                                  ? `${usuario.puesto_trabajo.color}33`
                                  : "#fff",
                              }}
                              onClick={() => setUsuarioModal(usuario)}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <span style={{ flex: 1 }}>
                                  {usuario.nombre}
                                  {usuario.apellidos &&
                                  usuario.apellidos !== usuario.nombre
                                    ? ` ${usuario.apellidos}`
                                    : ""}
                                </span>
                                {Object.values(
                                  usuario.asignacionesPorFecha,
                                ).some((d) => d.asignaciones?.length > 0) && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    sx={{ p: 0.25 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setUsuarioEliminarFila(usuario);
                                    }}
                                    title="Eliminar todas las asignaciones del mes"
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                              </Box>
                            </td>
                            {diasDelMes.map((dia) => {
                              const datosDia = usuario.asignacionesPorFecha[
                                dia.fecha
                              ] || { asignaciones: [], otrasSedes: [], ausencia: null };
                              const asignacionesDia = datosDia.asignaciones;
                              const otrasSedes = datosDia.otrasSedes;
                              const ausencia = datosDia.ausencia || null;
                              const tieneOtrasSedes = otrasSedes.length > 0;
                              const todasAsignaciones = [
                                ...asignacionesDia,
                                ...otrasSedes,
                              ];
                              // Buscar agrupaciones que coincidan con los turnos del día
                              const turnoIdsDia = new Set(
                                todasAsignaciones.map((a) => a.turno_id),
                              );
                              const simbolosSet = new Set();
                              const turnosCubiertos = new Set();
                              // Primero intentar agrupaciones multi-turno (más específicas)
                              [...agrupacionesSede]
                                .sort(
                                  (a, b) =>
                                    (b.turnos?.length || 0) -
                                    (a.turnos?.length || 0),
                                )
                                .forEach((agr) => {
                                  const turnosAgr = (agr.turnos || []).map(
                                    (t) => (typeof t === "object" ? t.id : t),
                                  );
                                  if (
                                    turnosAgr.length > 0 &&
                                    turnosAgr.every(
                                      (tid) =>
                                        turnoIdsDia.has(tid) &&
                                        !turnosCubiertos.has(tid),
                                    )
                                  ) {
                                    simbolosSet.add(agr.codigo);
                                    turnosAgr.forEach((tid) =>
                                      turnosCubiertos.add(tid),
                                    );
                                  }
                                });
                              // Turnos no cubiertos por ninguna agrupación
                              turnoIdsDia.forEach((tid) => {
                                if (!turnosCubiertos.has(tid)) {
                                  simbolosSet.add(mapaAgrupaciones[tid] || "?");
                                }
                              });
                              const simbolos = [...simbolosSet].join(" ");
                              const tooltipParts = [];
                              if (asignacionesDia.length > 0) {
                                tooltipParts.push(
                                  ...[...asignacionesDia]
                                    .sort((a, b) =>
                                      (a.hora_entrada || "").localeCompare(
                                        b.hora_entrada || "",
                                      ),
                                    )
                                    .map(
                                      (a) =>
                                        `${mapaAgrupaciones[a.turno_id] || "?"} (${a.hora_entrada || ""}-${a.hora_salida || ""})`,
                                    ),
                                );
                              }
                              if (tieneOtrasSedes) {
                                tooltipParts.push(
                                  ...[...otrasSedes]
                                    .sort((a, b) =>
                                      (a.hora_entrada || "").localeCompare(
                                        b.hora_entrada || "",
                                      ),
                                    )
                                    .map(
                                      (a) =>
                                        `⚠ ${a.sede_nombre ? a.sede_nombre + " / " : ""}${a.departamento_nombre} (${a.hora_entrada || ""}-${a.hora_salida || ""})`,
                                    ),
                                );
                              }
                              return (
                                <td
                                  key={dia.fecha}
                                  style={{
                                    ...tdStyle,
                                    background: ausencia
                                      ? (ausencia.nombrePermiso === "Vacaciones" ? "#81c784" : "#ff9800")
                                      : dia.fueraDeMes
                                        ? tieneOtrasSedes
                                          ? "#fff3e0"
                                          : "#f5f5f5"
                                        : tieneOtrasSedes
                                          ? "#fff3e0"
                                          : dia.esFinDeSemana
                                            ? "#f5f8ff"
                                            : "inherit",
                                    fontWeight: simbolos ? 600 : 400,
                                    color: tieneOtrasSedes
                                      ? "#e65100"
                                      : simbolos
                                        ? "#1565c0"
                                        : "#ccc",
                                  }}
                                  title={tooltipParts.join(", ")}
                                  onContextMenu={(e) => {
                                    if (
                                      tieneOtrasSedes &&
                                      asignacionesDia.length === 0
                                    )
                                      return;
                                    e.preventDefault();
                                    setMenuContextual({
                                      anchorPosition: {
                                        top: e.clientY,
                                        left: e.clientX,
                                      },
                                      usuario,
                                      fecha: dia.fecha,
                                    });
                                  }}
                                >
                                  <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                                    {simbolos || "–"}
                                    {ausencia && (
                                      <WarningAmberIcon
                                        titleAccess={ausencia.nombrePermiso ? `${ausencia.nombrePermiso} (${ausencia.estado})` : "Ausencia"}
                                        sx={{
                                          position: "absolute",
                                          top: -8,
                                          right: -6,
                                          fontSize: 13,
                                          color: "#b71c1c",
                                        }}
                                      />
                                    )}
                                  </Box>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        ) : (
          <Alert severity="info">No hay departamentos disponibles.</Alert>
        )}
      </CardContent>

      {/* Modal resumen empleado */}
      <Dialog
        open={!!usuarioModal}
        onClose={() => setUsuarioModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Resumen de {usuarioModal?.nombre}
          {usuarioModal?.apellidos &&
          usuarioModal.apellidos !== usuarioModal.nombre
            ? ` ${usuarioModal.apellidos}`
            : ""}{" "}
          — {MESES[mesActivo]} {anioSeleccionado}
        </DialogTitle>
        <DialogContent dividers>
          {resumenModal && (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Símbolo</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="right">
                      Días
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="right">
                      Horas
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumenModal.simbolos.map((s) => (
                    <TableRow key={s.codigo}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "1rem" }}>
                        {s.codigo}
                      </TableCell>
                      <TableCell align="right">{s.dias}</TableCell>
                      <TableCell align="right">{s.horas}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 2, display: "flex", gap: 4 }}>
                <Typography variant="body1">
                  <strong>Días que trabaja:</strong> {resumenModal.diasTrabaja}
                </Typography>
                <Typography variant="body1">
                  <strong>Días que no trabaja:</strong>{" "}
                  {resumenModal.diasNoTrabaja}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsuarioModal(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminación de fila */}
      <Dialog
        open={!!usuarioEliminarFila}
        onClose={() => setUsuarioEliminarFila(null)}
      >
        <DialogTitle>Eliminar asignaciones</DialogTitle>
        <DialogContent>
          Se van a eliminar <strong>todas</strong> las asignaciones de{" "}
          <strong>
            {usuarioEliminarFila?.nombre}
            {usuarioEliminarFila?.apellidos &&
            usuarioEliminarFila.apellidos !== usuarioEliminarFila.nombre
              ? ` ${usuarioEliminarFila.apellidos}`
              : ""}
          </strong>{" "}
          en {MESES[mesActivo]} {anioSeleccionado} correspondientes al
          departamento <strong>{departamentoActual?.nombre}</strong> de la sede{" "}
          <strong>{sedeActual?.nombre}</strong>. Esta acción no se puede
          deshacer.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsuarioEliminarFila(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleEliminarFila}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menú contextual para cambiar símbolo */}
      <Menu
        open={!!menuContextual}
        onClose={() => setMenuContextual(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuContextual?.anchorPosition}
      >
        <MenuItem
          onClick={() => {
            handleLiberarDia(menuContextual.usuario, menuContextual.fecha);
            setMenuContextual(null);
          }}
        >
          – Libre
        </MenuItem>
        {agrupacionesSede.map((agr) => (
          <MenuItem
            key={agr.id}
            onClick={() => {
              handleCambiarSimbolo(
                menuContextual.usuario,
                menuContextual.fecha,
                agr,
              );
              setMenuContextual(null);
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: agr.color || "#ccc",
                mr: 1,
              }}
            />
            {agr.codigo} — {agr.nombre}
          </MenuItem>
        ))}
      </Menu>

      {/* Dialog añadir trabajador */}
      <Dialog
        open={openAgregarTrabajador}
        onClose={() => setOpenAgregarTrabajador(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Añadir trabajador</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Buscar por nombre..."
            value={busquedaTrabajador}
            onChange={(e) => setBusquedaTrabajador(e.target.value)}
            sx={{ mb: 1 }}
          />
          {trabajadoresDisponibles.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2, textAlign: "center" }}
            >
              No hay trabajadores disponibles para añadir.
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
              {trabajadoresDisponibles.map((u) => (
                <ListItemButton
                  key={u.id_usuario}
                  onClick={() => handleAgregarTrabajador(u)}
                >
                  <ListItemText
                    primary={
                      u.nombre +
                      (u.apellidos && u.apellidos !== u.nombre
                        ? ` ${u.apellidos}`
                        : "")
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAgregarTrabajador(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default VistaMensualDepartamentos;
